/**
 * PSV-1888 — cosmetics: hull skins, engine trails, death effects.
 *
 * This table is the GAMEPLAY source of truth (render payloads + price
 * validation on the server-authoritative purchase path) and the seed for
 * the platform Catalog: `server/scripts/sync-catalog.mjs` upserts every
 * entry as a catalog item (sku = id, custom payload carries kind/color),
 * and the in-game shop renders the PUBLISHED STOREFRONT fetched from the
 * platform — the shared table only backs validation and offline fallback.
 *
 * Renders:
 *   skin  → hull sprite multiply-tint (overrides the class tint);
 *   trail → engine-exhaust particle color;
 *   death → explosion/debris palette on ShipKilled.
 */

export type CosmeticKind = 'skin' | 'trail' | 'death';

export interface CosmeticDef {
  /** Stable sku — persisted in Player.Cosmetics and used on the wire. */
  id: string;
  kind: CosmeticKind;
  name: string;
  priceGems: number;
  /** Render payload: tint / particle / palette base color. */
  color: number;
  /**
   * PSV-1906 — reward-only: cannot be purchased (the shop renders it as
   * a locked reward); granted by the server (ascension milestones,
   * season crowns).
   */
  reward?: true;
}

export const COSMETICS: readonly CosmeticDef[] = [
  // Hull skins — multiply tints tuned to stay readable on the sprite.
  { id: 'skin-crimson', kind: 'skin', name: 'Crimson Raider', priceGems: 40, color: 0xff6b6b },
  { id: 'skin-emerald', kind: 'skin', name: 'Emerald Wing', priceGems: 40, color: 0x51e0a1 },
  { id: 'skin-violet', kind: 'skin', name: 'Void Violet', priceGems: 60, color: 0xb086ff },
  { id: 'skin-gold', kind: 'skin', name: 'Gold Baron', priceGems: 120, color: 0xffd84a },
  { id: 'skin-ice', kind: 'skin', name: 'Deep Freeze', priceGems: 60, color: 0x7fd4ff },
  { id: 'skin-shadow', kind: 'skin', name: 'Shadow Ops', priceGems: 90, color: 0x8899aa },
  // Engine trails.
  { id: 'trail-plasma', kind: 'trail', name: 'Plasma Stream', priceGems: 30, color: 0x66ccff },
  { id: 'trail-ember', kind: 'trail', name: 'Ember Wake', priceGems: 30, color: 0xff9040 },
  { id: 'trail-toxic', kind: 'trail', name: 'Toxic Exhaust', priceGems: 50, color: 0x7dff5a },
  { id: 'trail-royal', kind: 'trail', name: 'Royal Wake', priceGems: 80, color: 0xcc88ff },
  // Death effects — explosion palette.
  { id: 'death-nova', kind: 'death', name: 'Micro Nova', priceGems: 70, color: 0xfff3b0 },
  { id: 'death-frost', kind: 'death', name: 'Frost Shatter', priceGems: 70, color: 0xa8e6ff },
  // PSV-1906 — reward-only (ascension milestones + season champion).
  { id: 'skin-ascendant', kind: 'skin', name: 'Ascendant', priceGems: 0, color: 0xffffff, reward: true },
  { id: 'trail-ascendant', kind: 'trail', name: 'Star Wake', priceGems: 0, color: 0xfff6c0, reward: true },
  { id: 'death-ascendant', kind: 'death', name: 'Supernova', priceGems: 0, color: 0xffd84a, reward: true },
  { id: 'skin-champion', kind: 'skin', name: 'Season Champion', priceGems: 0, color: 0xffd700, reward: true },
] as const;

const BY_ID = new Map(COSMETICS.map((c) => [c.id, c] as const));

export function cosmeticById(id: unknown): CosmeticDef | null {
  return typeof id === 'string' ? (BY_ID.get(id) ?? null) : null;
}

/** Equipped slots + ownership, persisted as ONE flat JSON string field
 * (`Player.Cosmetics`) — parts are replace-not-merge on the platform
 * (PSV-1898 finding), a scalar field sidesteps that entirely. */
export interface CosmeticsState {
  owned: string[];
  skin: string; // '' = default hull
  trail: string; // '' = default exhaust
  death: string; // '' = default explosion
}

export function emptyCosmetics(): CosmeticsState {
  return { owned: [], skin: '', trail: '', death: '' };
}

export function serializeCosmetics(c: CosmeticsState): string {
  return JSON.stringify(c);
}

/** Tolerant parse — garbage/missing → empty state (first-time player). */
export function parseCosmeticsField(raw: unknown): CosmeticsState {
  if (typeof raw !== 'string' || raw === '') return emptyCosmetics();
  try {
    const parsed = JSON.parse(raw) as Partial<CosmeticsState> | null;
    if (parsed === null || typeof parsed !== 'object') return emptyCosmetics();
    const owned = Array.isArray(parsed.owned)
      ? parsed.owned.filter((id): id is string => typeof id === 'string' && BY_ID.has(id))
      : [];
    const slot = (v: unknown): string =>
      typeof v === 'string' && (v === '' || owned.includes(v)) ? v : '';
    return {
      owned,
      skin: slot(parsed.skin),
      trail: slot(parsed.trail),
      death: slot(parsed.death),
    };
  } catch {
    return emptyCosmetics();
  }
}
